import React, { useEffect, useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { Button, Checkbox, Input, Select, Tooltip } from 'antd';
import { informationEditSchema, InformationEditFormData } from '@/schemas/informationSchema';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_BOTH } from '@/constants/serviceType';
import { HOUR_OPTIONS, MINUTE_OPTIONS } from '@/constants/timeOptions';
import { useTenantSiteTypes } from '@/hooks/admin/useTenantSiteTypes';
import { useInformationClinicOptions } from '@/hooks/admin/useInformationClinicOptions';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import { AppSelect, AppAdminFormLabel } from '@/components/atoms';
import { convertSeirekiWrap } from '@/utils/wareki';
import { partsToTime, timeToParts } from '@/utils/timeParts';
import useAppToast from '@/hooks/useAppToast';
import { AdminInformationItem } from '@/services/admin/informations';

const { TextArea } = Input;

/**
 * Parse "YYYY-MM-DD HH:MM" into { date, time } parts.
 */
function splitDateTime(dt: string | null | undefined): { date: string | null; time: string | null } {
  if (!dt) return { date: null, time: null };
  const parts = dt.split(' ');
  return { date: parts[0] || null, time: parts[1] || null };
}

function resolveServiceFlags(serviceType: string | null): { a2: boolean; r2: boolean } {
  const upper = (serviceType ?? '').toUpperCase();
  return {
    a2: upper === SERVICE_TYPE_A2 || upper === SERVICE_TYPE_BOTH,
    r2: upper === SERVICE_TYPE_R2 || upper === SERVICE_TYPE_BOTH,
  };
}

interface AdminInformationEditFormProps {
  siteType: string;
  initialData: AdminInformationItem;
  onSubmit: (data: InformationEditFormData) => void;
  isSubmitting?: boolean;
  onPreview?: (data: { title: string; information_date: string; contents: string | null }) => void;
}

const AdminInformationEditForm: React.FC<AdminInformationEditFormProps> = ({
  siteType,
  initialData,
  onSubmit,
  isSubmitting = false,
  onPreview,
}) => {
  const toast = useAppToast();
  const displayFrom = splitDateTime(initialData.display_from);
  const displayTo = splitDateTime(initialData.display_to);
  const serviceFlags = resolveServiceFlags(initialData.service_type);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useTrimmedForm<InformationEditFormData>({
    resolver: zodResolver(informationEditSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      title: initialData.title ?? '',
      information_date: initialData.information_date ?? '',
      contents: initialData.contents ?? '',
      display_from_date: displayFrom.date,
      display_from_time: displayFrom.time,
      display_to_date: displayTo.date,
      display_to_time: displayTo.time,
      is_display: initialData.is_display ?? false,
      service_type_a2: serviceFlags.a2,
      service_type_r2: serviceFlags.r2,
      tenant_ids: (initialData.tenant_ids ?? []).map(Number),
      clinic_ids: (initialData.clinic_ids ?? []).map(Number),
    },
  });

  // Super-admin sees all tenants; other admins see only their family
  const { admin } = useSiteAdminAuth();
  const tenantQueryScope = admin?.is_full_admin ? undefined : siteType;

  // Fetch available tenants for multi-select
  const { data: tenantSiteTypesData } = useTenantSiteTypes(tenantQueryScope);
  const tenantItems = tenantSiteTypesData?.data?.site_types ?? [];
  const tenantOptions = useMemo(
    () => tenantItems.map(item => ({ value: item.id, label: item.name })),
    [tenantItems]
  );

  // Show tenant selector only for super-admin or medix site (consistent with list page canUseSiteTypeFeatures)
  const showTenantSelector = siteType === 'super-admin' || siteType === 'medix';

  // Auto-set tenant when selector is hidden (hitowa etc.) and tenant_ids is empty
  useEffect(() => {
    if (showTenantSelector || tenantItems.length === 0) return;
    const currentTenantIds = watch('tenant_ids');
    if (currentTenantIds.length > 0) return;
    const matched = tenantItems.find(t => t.site_type === siteType);
    if (matched) setValue('tenant_ids', [matched.id]);
  }, [tenantItems, showTenantSelector, siteType, setValue, watch]);

  // Watch selected tenant_ids to control clinic selector
  const selectedTenantIds = watch('tenant_ids');

  // Infinite-scroll clinic options filtered by selected tenants
  const {
    data: clinicPages,
    isFetching: isLoadingClinics,
    fetchNextPage,
    hasNextPage,
  } = useInformationClinicOptions(siteType, selectedTenantIds, {
    enabled: selectedTenantIds.length > 0,
  });

  // Flatten all loaded pages into a single list
  const allClinicOptions = useMemo(
    () => clinicPages?.pages.flatMap(p => p.data) ?? [],
    [clinicPages]
  );

  // Seed clinic map from initialData.clinic_details (ensures labels for pre-selected clinics)
  const initialClinicDetails = initialData.clinic_details ?? [];
  const clinicSiteTypeMapRef = useRef<Map<number, string>>(
    new Map(initialClinicDetails.map(c => [c.id, c.site_type ?? '']))
  );

  // Accumulate clinic → site_type mapping across all loaded pages
  useEffect(() => {
    allClinicOptions.forEach(c => {
      clinicSiteTypeMapRef.current.set(c.id, c.site_type ?? '');
    });
  }, [allClinicOptions]);

  // Merge initial clinic details with infinite-scroll options (dedup by id)
  const clinicSelectOptions = useMemo(() => {
    const map = new Map<number, string>();
    initialClinicDetails.forEach(c => map.set(c.id, c.clinic_number));
    allClinicOptions.forEach(c => map.set(c.id, c.clinic_number));
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [allClinicOptions, initialClinicDetails]);

  // Track previous tenant selection to detect removals
  const prevTenantIdsRef = useRef<number[]>(initialData.tenant_ids ?? []);
  useEffect(() => {
    const prev = prevTenantIdsRef.current;
    const curr = selectedTenantIds;
    const removedTenantIds = prev.filter(id => !curr.includes(id));

    if (removedTenantIds.length > 0) {
      const removedSiteTypes = new Set(
        tenantItems.filter(t => removedTenantIds.includes(t.id)).map(t => t.site_type)
      );
      if (removedSiteTypes.size > 0) {
        const currentClinicIds = watch('clinic_ids') ?? [];
        const filtered = currentClinicIds.filter(id => {
          const st = clinicSiteTypeMapRef.current.get(id);
          return !st || !removedSiteTypes.has(st);
        });
        if (filtered.length !== currentClinicIds.length) {
          setValue('clinic_ids', filtered);
        }
      }
    }

    prevTenantIdsRef.current = curr;
  }, [selectedTenantIds, tenantItems, setValue, watch]);

  // Load next page when user scrolls to bottom of dropdown
  const handleClinicPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;
    if (nearBottom && hasNextPage && !isLoadingClinics) {
      fetchNextPage();
    }
  };

  // Clear field error when that field changes
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) return;
      clearErrors(name as keyof InformationEditFormData);
      if (name === 'service_type_r2') clearErrors('service_type_a2');
      if (name === 'display_from_date') clearErrors('display_from_time');
      if (name === 'display_to_date') clearErrors('display_to_time');
      if (
        ['display_from_date', 'display_from_time', 'display_to_date', 'display_to_time'].includes(
          name
        )
      ) {
        clearErrors('display_to_date');
        clearErrors('display_from_date');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  // Watch date fields to control time field disabled state
  const displayFromDate = watch('display_from_date');
  const displayToDate = watch('display_to_date');

  useEffect(() => {
    if (!displayFromDate) setValue('display_from_time', null);
  }, [displayFromDate, setValue]);

  useEffect(() => {
    if (!displayToDate) setValue('display_to_time', null);
  }, [displayToDate, setValue]);

  const renderError = (fieldName: string) => {
    const parts = fieldName.split('.');
    let error: any = errors;
    for (const part of parts) {
      error = error?.[part];
    }
    if (!error) return null;
    return <div className="text-red-500 text-sm mt-1">{error.message as string}</div>;
  };

  return (
    <form
      onSubmit={e => {
        const currentTitle = getValues('title') ?? '';
        const trimmedTitle = currentTitle.trim();
        if (currentTitle !== trimmedTitle) {
          setValue('title', trimmedTitle, { shouldDirty: true });
        }
        void handleSubmit(onSubmit, () => toast.error('エラーが発生しました。'))(e);
      }}
      className="space-y-6"
    >
      {onPreview && (
        <div className="mb-4">
          <Button
            type="primary"
            onClick={() => {
              const values = watch();
              onPreview({
                title: values.title,
                information_date: values.information_date,
                contents: values.contents ?? null,
              });
            }}
          >
            プレビュー
          </Button>
        </div>
      )}

      {/* #4 表示対象 — hidden for single-tenant routes (e.g. hitowa), auto-set via useEffect */}
      {showTenantSelector && (
        <div>
          <AppAdminFormLabel label="表示対象" required />
          <Controller
            name="tenant_ids"
            control={control}
            render={({ field }) => (
              <Select
                mode="multiple"
                value={field.value}
                onChange={val => field.onChange(val)}
                options={tenantOptions}
                placeholder="サイト区分"
                className="w-full"
                status={errors.tenant_ids ? 'error' : undefined}
                aria-label="表示対象"
                maxTagCount="responsive"
              />
            )}
          />
          {renderError('tenant_ids')}
        </div>
      )}

      {/* #5 表示サービス + #14 表示設定 — same row, 2 cols */}
      <div className="grid grid-cols-2 items-start">
        <div>
          <AppAdminFormLabel label="表示サービス" required />
          <div className="flex gap-6">
            <Controller
              name="service_type_a2"
              control={control}
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  {SERVICE_TYPE_A2} (はり・きゅう)
                </Checkbox>
              )}
            />
            <Controller
              name="service_type_r2"
              control={control}
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  {SERVICE_TYPE_R2} (マッサージ)
                </Checkbox>
              )}
            />
          </div>
          {renderError('service_type_a2')}
        </div>

        <div>
          <AppAdminFormLabel label="表示設定" />
          <Controller
            name="is_display"
            control={control}
            render={({ field }) => (
              <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)} />
            )}
          />
        </div>
      </div>

      {/* #6 対象治療院ID */}
      <div>
        <AppAdminFormLabel label="対象治療院ID" />
        <Controller
          name="clinic_ids"
          control={control}
          render={({ field }) => (
            <Select
              mode="multiple"
              value={field.value}
              onChange={val => field.onChange(val)}
              options={clinicSelectOptions}
              loading={isLoadingClinics}
              disabled={selectedTenantIds.length === 0}
              placeholder={selectedTenantIds.length === 0 ? 'サイトを先に選択してください' : ''}
              className="w-full"
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
              onPopupScroll={handleClinicPopupScroll}
              aria-label="対象治療院ID"
              maxTagCount="responsive"
              maxTagPlaceholder={omittedValues => {
                const allLabels = field.value
                  .map(id => clinicSelectOptions.find(o => o.value === id)?.label)
                  .filter(Boolean)
                  .join(', ');
                return (
                  <Tooltip title={allLabels}>
                    <span>+{omittedValues.length} ...</span>
                  </Tooltip>
                );
              }}
            />
          )}
        />
      </div>

      {/* #7 タイトル + #8 お知らせ日 — same row, 2 cols */}
      <div className="grid grid-cols-2 items-start gap-6">
        <div>
          <AppAdminFormLabel label="タイトル" required />
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input {...field} status={errors.title ? 'error' : undefined} aria-label="タイトル" />
            )}
          />
          {renderError('title')}
        </div>

        <div>
          <AppAdminFormLabel label="お知らせ日" required />
          <Controller
            name="information_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || ''}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  field.onChange(seireki || '');
                }}
                picker="day"
                className="w-full"
                placeholder=""
                error={!!errors.information_date}
              />
            )}
          />
          {renderError('information_date')}
        </div>
      </div>

      {/* #9 お知らせ内容 */}
      <div>
        <AppAdminFormLabel label="お知らせ内容" />
        <Controller
          name="contents"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              value={field.value ?? ''}
              rows={10}
              status={errors.contents ? 'error' : undefined}
              aria-label="お知らせ内容"
            />
          )}
        />
        {renderError('contents')}
      </div>

      {/* #10-13 有効期限 */}
      <div>
        <AppAdminFormLabel label="有効期限" />
        <div className="flex items-center gap-2">
          <Controller
            name="display_from_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || ''}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  field.onChange(seireki || null);
                }}
                picker="day"
                className="flex-1"
                placeholder=""
                error={!!errors.display_from_date}
              />
            )}
          />
          <Controller
            name="display_from_time"
            control={control}
            render={({ field }) => {
              const parts = timeToParts(field.value);
              return (
                <div className="flex items-center gap-1">
                  <AppSelect
                    className="min-w-[56px]"
                    options={HOUR_OPTIONS}
                    value={parts != null ? parts.hour : ''}
                    onChange={v => {
                      const h = v === '' ? null : Number(v);
                      field.onChange(partsToTime(h, parts?.minute ?? 0));
                    }}
                    disabled={!displayFromDate}
                    status={errors.display_from_time ? 'error' : undefined}
                  />
                  <span className="text-gray-500">:</span>
                  <AppSelect
                    className="min-w-[56px]"
                    options={MINUTE_OPTIONS}
                    value={parts != null ? parts.minute : ''}
                    onChange={v => {
                      const m = v === '' ? null : Number(v);
                      field.onChange(partsToTime(parts?.hour ?? 0, m));
                    }}
                    disabled={!displayFromDate}
                    status={errors.display_from_time ? 'error' : undefined}
                  />
                </div>
              );
            }}
          />
          <span>-</span>
          <Controller
            name="display_to_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || ''}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  field.onChange(seireki || null);
                }}
                picker="day"
                className="flex-1"
                placeholder=""
                error={!!errors.display_to_date}
              />
            )}
          />
          <Controller
            name="display_to_time"
            control={control}
            render={({ field }) => {
              const parts = timeToParts(field.value);
              return (
                <div className="flex items-center gap-1">
                  <AppSelect
                    className="min-w-[56px]"
                    options={HOUR_OPTIONS}
                    value={parts != null ? parts.hour : ''}
                    onChange={v => {
                      const h = v === '' ? null : Number(v);
                      field.onChange(partsToTime(h, parts?.minute ?? 0));
                    }}
                    disabled={!displayToDate}
                    status={errors.display_to_time ? 'error' : undefined}
                  />
                  <span className="text-gray-500">:</span>
                  <AppSelect
                    className="min-w-[56px]"
                    options={MINUTE_OPTIONS}
                    value={parts != null ? parts.minute : ''}
                    onChange={v => {
                      const m = v === '' ? null : Number(v);
                      field.onChange(partsToTime(parts?.hour ?? 0, m));
                    }}
                    disabled={!displayToDate}
                    status={errors.display_to_time ? 'error' : undefined}
                  />
                </div>
              );
            }}
          />
        </div>
        {renderError('display_from_date')}
        {renderError('display_from_time')}
        {renderError('display_to_time')}
        {renderError('display_to_date')}
      </div>

      {/* #15 更新 */}
      <div className="flex justify-end">
        <Button type="primary" htmlType="submit" loading={isSubmitting} className="min-w-[120px]">
          更新
        </Button>
      </div>
    </form>
  );
};

export default AdminInformationEditForm;
